import React, { useEffect, useState } from 'react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  MdMenu, MdLogout, MdChevronRight, MdChevronLeft, MdDashboard,
  MdApartment, MdSettings, MdStar, MdAutoAwesome, MdTour, MdHelpOutline, MdStorage, MdPublic, MdHelpCenter,
} from 'react-icons/md';
import { useAuth } from './auth';
import useSounds from '../hooks/useSounds';
import { CacheToggle } from '../ui/cache';
import { LiveBadge, OfflineBanner } from '../ui/offline/LiveStatus';
import GlobalSearch from './search/GlobalSearch';
import useSchoolYear from './useSchoolYear';
import { CREDIT_LINE, yearLabel } from './about';
import SkinToggle from '../skin/SkinToggle';
import './shell.css';

const GROUP_ICON = {
  'العمل اليومي': MdDashboard,
  المدرسة: MdApartment,
  'البيانات الأساسية': MdStorage,
  'إدارة النظام': MdSettings,
  'بوابة مشكاة': MdPublic,
  'المساعدة والتطوير': MdHelpCenter,
};

const ROLE_LABEL = {
  admin: 'مدير', register: 'موجه', teacher: 'معلم', student: 'طالب / ولي أمر',
};

const COLLAPSE_KEY = 'mtz.navCollapsed';

const initials = (name) => {
  const w = String(name || '').trim().split(/\s+/);
  return w.length >= 2 ? w[0][0] + w[1][0] : (w[0] || 'م')[0];
};

function NavLink({ item, active, onClick, onTip }) {
  return (
    <button
      type="button"
      className={`shell-link${item.featured ? ' is-featured' : ''}${active ? ' is-active' : ''}${item.disabled ? ' is-disabled' : ''}`}
      onClick={onClick}
      onMouseEnter={(e) => onTip(item.label, e.currentTarget)}
      onMouseLeave={() => onTip(null)}
      aria-label={item.label}
      aria-current={active ? 'page' : undefined}
      aria-disabled={item.disabled || undefined}
      disabled={item.disabled}
      title={item.disabled ? 'قريباً' : undefined}
      data-guide={`nav-${item.id}`}
    >
      <span className="shell-link-icon"><item.icon /></span>
      <span className="shell-link-label">{item.label}</span>
    </button>
  );
}

/**
 * App frame: sidebar + top bar + content. `nav` is the already
 * role-filtered list of sections, so the shell never decides
 * permissions itself.
 *
 * The sidebar follows the old system's layout the office is used to: a
 * gradient panel with the daily pages («الصفحات الأساسية») set apart at
 * the top and the rest grouped below. It collapses to an icon rail
 * (remembered per browser) to give wide tables the whole screen.
 */
export default function Shell({
  nav, active, onNavigate, onReplayIntro, onOpenTour, children,
}) {
  const { user, logout } = useAuth();
  const sounds = useSounds();
  const year = useSchoolYear();
  const [open, setOpen] = useState(false);
  // The collapsed rail's hover label. Fixed-position and drawn outside
  // the nav, so the rail can keep scrolling (a CSS ::after label would
  // need overflow:visible, which cut off the lower icons).
  const [tip, setTip] = useState(null);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0'); } catch { /* ignore */ }
  }, [collapsed]);

  const featured = nav.filter((n) => n.featured);
  const groups = nav.filter((n) => !n.featured).reduce((acc, item) => {
    (acc[item.group] = acc[item.group] || []).push(item);
    return acc;
  }, {});

  const go = (id, intent) => {
    if (id !== active) sounds.pageChange();
    onNavigate(id, intent);
    setOpen(false);
  };
  const showTip = (label, el) => {
    if (!collapsed || !label) { setTip(null); return; }
    const r = el.getBoundingClientRect();
    setTip({ label, top: r.top + r.height / 2, right: window.innerWidth - r.left + 10 });
  };
  const activeItem = nav.find((n) => n.id === active);
  const roleLabel = ROLE_LABEL[user?.role] || '';

  return (
    <div className={`shell${collapsed ? ' is-collapsed' : ''}`}>
      <aside className={`shell-side${open ? ' is-open' : ''}`}>
        <div className="shell-brand">
          <div className="shell-logo">م</div>
          <div className="shell-brand-text">
            <strong>المتميزون</strong>
            <span>نظام إدارة المؤسسة التعليمية</span>
          </div>
        </div>

        <button
          type="button"
          className="shell-collapse"
          onClick={() => { setCollapsed((c) => !c); setTip(null); }}
          title={collapsed ? 'توسيع القائمة' : 'طي القائمة'}
          aria-label={collapsed ? 'توسيع القائمة' : 'طي القائمة'}
        >
          {collapsed ? <MdChevronLeft size={18} /> : <MdChevronRight size={18} />}
        </button>

        <nav className="shell-nav">
          {featured.length > 0 && (
            <div className="shell-group is-featured">
              <div className="shell-group-title">
                <span className="shell-star"><MdStar size={12} /></span>
                <span>الصفحات الأساسية</span>
              </div>
              {featured.map((item) => (
                <NavLink key={item.id} item={item} active={active === item.id} onClick={() => !item.disabled && go(item.id)} onTip={showTip} />
              ))}
            </div>
          )}

          {Object.entries(groups).map(([group, items]) => {
            const GIcon = GROUP_ICON[group] || MdDashboard;
            return (
              <div key={group} className="shell-group">
                <div className="shell-group-title">
                  <GIcon size={13} />
                  <span>{group}</span>
                </div>
                {items.map((item) => (
                  <NavLink key={item.id} item={item} active={active === item.id} onClick={() => !item.disabled && go(item.id)} onTip={showTip} />
                ))}
              </div>
            );
          })}
        </nav>

        <button type="button" className="shell-credit" onClick={() => go('about')}>
          {CREDIT_LINE}
        </button>

        <div className="shell-side-foot">
          <div className="shell-avatar" title={user?.name}>{initials(user?.name)}</div>
          <div className="shell-user">
            <strong>{user?.name}</strong>
            <span>{roleLabel}</span>
          </div>
          {onReplayIntro && (
            <button
              type="button"
              className="shell-logout shell-intro"
              onClick={() => { setOpen(false); onReplayIntro(); }}
              title="جولة الترحيب"
              aria-label="جولة الترحيب"
            >
              <MdAutoAwesome size={18} />
            </button>
          )}
          <button type="button" className="shell-logout" onClick={logout} title="تسجيل الخروج" aria-label="تسجيل الخروج">
            <MdLogout size={18} />
          </button>
        </div>
      </aside>

      {tip && <div className="shell-tip" style={{ top: tip.top, right: tip.right }}>{tip.label}</div>}

      {open && <div className="shell-scrim" onClick={() => setOpen(false)} />}

      <div className="shell-main">
        <header className="shell-top">
          <button type="button" className="shell-burger" onClick={() => setOpen(true)} aria-label="القائمة" data-guide="nav-burger">
            <MdMenu size={20} />
          </button>
          <div className="shell-top-heading">
            {activeItem && <span className="shell-top-icon"><activeItem.icon /></span>}
            <div className="shell-top-text">
              <span className="shell-top-crumb">{activeItem?.featured ? 'الصفحات الأساسية' : activeItem?.group}</span>
              <span className="shell-top-title">{activeItem?.label || ''}</span>
            </div>
          </div>
          <GlobalSearch nav={nav.filter((item) => !item.disabled)} onNavigate={go} />
          <div className="shell-top-tools">
            {/* «اكتشف بوابة مشكاة»: every tour station, any time. */}
            {onOpenTour && (
              <button type="button" className="shell-tour" onClick={onOpenTour} title="محطات بوابة مشكاة">
                <MdTour size={17} />
                <span>الجولة</span>
              </button>
            )}
            {/* «دليل الاستخدام»: how to do each job, with «ورّيني». */}
            {nav.some((n) => n.id === 'help') && (
              <button type="button" className="shell-tour shell-help" onClick={() => go('help')} title="دليل الاستخدام" data-guide="top-help">
                <MdHelpOutline size={17} />
                <span>كيف أعمل؟</span>
              </button>
            )}
            {year && (
              <button type="button" className="shell-year" onClick={() => go('about')} title="العام الدراسي">
                <span>العام الدراسي</span>
                <strong dir="ltr">{yearLabel(year.title)}</strong>
              </button>
            )}
            <SkinToggle />
            <LiveBadge />
            {/* Cache mode lives in the top bar, visible from every screen:
                whether you are looking at fresh or cached data changes how
                much you trust what is on the page, so it must never be
                buried in a settings menu. */}
            <CacheToggle className="shell-top-cache" />
          </div>
        </header>
        <OfflineBanner />
        {/* Keyed on the page so each page fades in on arrival. */}
        <main className="shell-content" key={active}>{children}</main>
      </div>

      <ToastContainer
        position="bottom-left"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
}
